// Client-side state helpers: language, theme, routing, and local analytics
// (events + sessions) with best-effort sync to the backend.
// Faithfully reconstructed from the deployed build.

import type { Lang } from "./i18n";
import { createSession, updateSession, postEvent } from "./api";

const LANG_KEY = "kd_lang";
const THEME_KEY = "kd_theme";
const ANALYTICS_KEY = "kd_analytics_v1";

export type Theme = "dark" | "light";

// --- Language ---
export function getInitialLang(): Lang {
  try {
    return ((localStorage.getItem(LANG_KEY) || sessionStorage.getItem(LANG_KEY)) as Lang) || "ar";
  } catch {
    return "ar";
  }
}
export function persistLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_KEY, lang);
    sessionStorage.setItem(LANG_KEY, lang);
  } catch {
    /* ignore */
  }
  document.documentElement.lang = lang === "ar" ? "ar" : "en";
  document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
}

// --- Theme ---
export function getInitialTheme(): Theme {
  try {
    return ((localStorage.getItem(THEME_KEY) || sessionStorage.getItem(THEME_KEY)) as Theme) || "dark";
  } catch {
    return "dark";
  }
}
export function persistTheme(theme: Theme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
    sessionStorage.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
  document.documentElement.classList.toggle("light", theme === "light");
}

// --- Routing (hash / /admin path) ---
export function getRoute(): string {
  if (typeof window === "undefined") return "";
  if (window.location.pathname.replace(/\/+$/, "").toLowerCase().endsWith("/admin")) return "admin";
  return window.location.hash.replace("#", "").trim().toLowerCase();
}
export function exitAdminRoute(): void {
  const target = window.location.pathname.replace(/\/admin\/?$/i, "").replace(/\/+$/, "") || "/";
  if (window.location.hash) history.replaceState(null, "", window.location.pathname);
  history.pushState(null, "", target);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

// --- Splash ---
export function showSplash(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("intro") === "off") return false;
  } catch {
    /* ignore */
  }
  return true;
}

// --- Local analytics (events + sessions) ---
export interface AnalyticsEvent {
  id: string;
  type: string;
  timestamp: number;
  email: string;
  name: string;
  meta?: unknown;
}
export interface OnboardingSession {
  email: string;
  name: string;
  role: string;
  careerPath: string;
  startedAt: number;
  completedAt?: number;
  durationMs?: number;
  rating?: number;
  ratingComment?: string;
}
interface AnalyticsStore {
  events: AnalyticsEvent[];
  sessions: OnboardingSession[];
}

function loadStore(): AnalyticsStore {
  try {
    const raw = localStorage.getItem(ANALYTICS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        events: Array.isArray(parsed.events) ? parsed.events : [],
        sessions: Array.isArray(parsed.sessions) ? parsed.sessions : [],
      };
    }
  } catch {
    /* ignore */
  }
  return { events: [], sessions: [] };
}
const ANALYTICS_CHANGED = "kd-analytics-changed";
function saveStore(store: AnalyticsStore): void {
  if (store.events.length > 500) store.events = store.events.slice(-500);
  if (store.sessions.length > 200) store.sessions = store.sessions.slice(-200);
  try {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(store));
    window.dispatchEvent(new Event(ANALYTICS_CHANGED));
  } catch {
    /* ignore */
  }
}

/** Admin: read the local analytics store (events + sessions). */
export function getAnalyticsStore(): AnalyticsStore {
  return loadStore();
}
export function subscribeAnalytics(cb: () => void): () => void {
  window.addEventListener(ANALYTICS_CHANGED, cb);
  return () => window.removeEventListener(ANALYTICS_CHANGED, cb);
}
export function clearAnalytics(): void {
  try {
    localStorage.removeItem(ANALYTICS_KEY);
    window.dispatchEvent(new Event(ANALYTICS_CHANGED));
  } catch {
    /* ignore */
  }
}
/** Export the analytics store (events + sessions) as CSV. */
export function exportAnalyticsCSV(): string {
  const store = loadStore();
  const rows: string[][] = [["Type", "Timestamp", "Email", "Name", "Detail"]];
  for (const s of store.sessions) {
    rows.push(["session", new Date(s.startedAt).toISOString(), s.email, s.name, `path=${s.careerPath} completed=${s.completedAt ? new Date(s.completedAt).toISOString() : ""} rating=${s.rating ?? ""}`]);
  }
  for (const e of store.events) {
    rows.push(["event:" + e.type, new Date(e.timestamp).toISOString(), e.email, e.name, JSON.stringify(e.meta ?? "")]);
  }
  return rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
}
function genId(): string {
  return Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

export function logEvent(type: string, opts: { email?: string; name?: string; meta?: unknown } = {}): void {
  const store = loadStore();
  store.events.push({ id: genId(), type, timestamp: Date.now(), email: opts.email || "", name: opts.name || "", meta: opts.meta });
  saveStore(store);
  // best-effort backend telemetry
  postEvent({ type, email: opts.email || "", name: opts.name || "", meta: opts.meta })?.catch?.(() => {});
}

export function startSession(info: { email: string; name: string; role: string }): OnboardingSession {
  const store = loadStore();
  const existing = store.sessions.find((s) => s.email === info.email && !s.completedAt && Date.now() - s.startedAt < 3600 * 1000);
  if (existing) return existing;
  const session: OnboardingSession = { email: info.email, name: info.name, role: info.role, careerPath: "", startedAt: Date.now() };
  store.sessions.push(session);
  saveStore(store);
  createSession(session)?.catch?.(() => {});
  return session;
}

export function setSessionCareerPath(email: string, careerPath: string): void {
  const store = loadStore();
  const session = [...store.sessions].reverse().find((s) => s.email === email && !s.completedAt);
  if (session) {
    session.careerPath = careerPath;
    saveStore(store);
    updateSession(email, { careerPath })?.catch?.(() => {});
  }
}

/** Marks the session complete and returns it (with `completedAt` set) so the
 *  caller can compute journey duration (e.g. for the manager-notification
 *  email's {{DURATION}} token) without a second store lookup. */
export function completeSession(email: string): OnboardingSession | null {
  const store = loadStore();
  const session = [...store.sessions].reverse().find((s) => s.email === email && !s.completedAt);
  if (session) {
    session.completedAt = Date.now();
    saveStore(store);
    updateSession(email, { completedAt: session.completedAt })?.catch?.(() => {});
  }
  return session || null;
}

/** Whether the (most recent) session for this email has already been rated. */
export function hasRated(email: string): boolean {
  const store = loadStore();
  const session = [...store.sessions].reverse().find((s) => s.email === email);
  return !!session && typeof session.rating === "number" && session.rating > 0;
}

export function submitRating(email: string, rating: number, comment: string): void {
  const store = loadStore();
  const session = [...store.sessions].reverse().find((s) => s.email === email) || null;
  if (session) {
    session.rating = rating;
    session.ratingComment = comment;
    saveStore(store);
  }
  updateSession(email, { rating, ratingComment: comment })?.catch?.(() => {});
  logEvent("rating", { email, meta: { rating, comment } });
}
